import { DP_EPSILON, MIN_SAMPLE_GAP, STROKE_T } from './constants';
import type { Pt } from './level';

export type Blocker =
  | { kind: 'poly'; pts: Pt[] }
  | { kind: 'circle'; c: Pt; r: number }
  | { kind: 'path'; pts: Pt[]; r: number };  // 이미 놓인 선(폴리라인 + 반두께)

export type StrokeSegment =
  | { kind: 'dot'; c: Pt }
  | { kind: 'line'; pts: Pt[] };

function distSq(a: Pt, b: Pt): number {
  const dx = a[0] - b[0], dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

/** 연속 점 사이 최소 간격. 마지막 점은 항상 남긴다. */
export function filterGap(pts: Pt[], gap: number): Pt[] {
  if (pts.length < 2) return pts.slice();
  const out: Pt[] = [pts[0]!];
  for (let i = 1; i < pts.length; i++) {
    if (distSq(pts[i]!, out[out.length - 1]!) >= gap * gap) out.push(pts[i]!);
  }
  const last = pts[pts.length - 1]!;
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

function segDist(p: Pt, a: Pt, b: Pt): number {
  const abx = b[0] - a[0], aby = b[1] - a[1];
  const l2 = abx * abx + aby * aby;
  let t = l2 === 0 ? 0 : ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (a[0] + t * abx), p[1] - (a[1] + t * aby));
}

/** Douglas-Peucker. */
export function simplify(pts: Pt[], eps: number): Pt[] {
  if (pts.length <= 2) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = 1; keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    let best = -1, bestD = eps;
    for (let i = s + 1; i < e; i++) {
      const d = segDist(pts[i]!, pts[s]!, pts[e]!);
      if (d > bestD) { bestD = d; best = i; }
    }
    if (best >= 0) { keep[best] = 1; stack.push([s, best], [best, e]); }
  }
  return pts.filter((_, i) => keep[i] === 1);
}

function pointInPoly(p: Pt, poly: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!, b = poly[j]!;
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

function distToPoly(p: Pt, poly: Pt[]): number {
  let d = Infinity;
  for (let i = 0; i < poly.length; i++) d = Math.min(d, segDist(p, poly[i]!, poly[(i + 1) % poly.length]!));
  return d;
}

export function isBlocked(p: Pt, blockers: Blocker[], margin: number): boolean {
  for (const b of blockers) {
    if (b.kind === 'circle') {
      if (distSq(p, b.c) < (b.r + margin) * (b.r + margin)) return true;
    } else if (b.kind === 'path') {
      if (b.pts.length === 1) { if (distSq(p, b.pts[0]!) < (b.r + margin) * (b.r + margin)) return true; continue; }
      for (let i = 1; i < b.pts.length; i++) if (segDist(p, b.pts[i - 1]!, b.pts[i]!) < b.r + margin) return true;
    } else {
      if (pointInPoly(p, b.pts) || distToPoly(p, b.pts) < margin) return true;
    }
  }
  return false;
}

/** 첫 차단 점 앞까지만 남긴다 — 땅·공에 닿으면 거기서 손을 뗀 것처럼(사용자 확인). */
export function truncateAtBlock(raw: Pt[], blockers: Blocker[]): Pt[] {
  const pts = densify(raw, MIN_SAMPLE_GAP * 2);
  const i = pts.findIndex((p) => isBlocked(p, blockers, STROKE_T / 2));
  return i < 0 ? pts : pts.slice(0, i);
}

function segsIntersect(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b) && o(a, b, c) !== 0 && o(c, d, a) !== 0;
}

/** 마지막 선분이 앞선 선분과 교차하는가 — 그리는 중 한 점씩 검사할 때 쓴다.
 *  점열은 filterGap 으로 간격을 둔 것이어야 하고, 바로 앞 두 선분(손 떨림)은 검사하지 않는다. */
export function lastSegmentCrosses(pts: Pt[]): boolean {
  const n = pts.length;
  if (n < 5) return false;
  const a = pts[n - 2]!, b = pts[n - 1]!;
  for (let i = 0; i + 1 < n - 3; i++) {
    if (segsIntersect(pts[i]!, pts[i + 1]!, a, b)) return true;
  }
  return false;
}

/** 점열 전체의 자기 교차 여부(간격 필터 후). */
export function selfIntersects(raw: Pt[]): boolean {
  const pts = filterGap(densify(raw, MIN_SAMPLE_GAP), MIN_SAMPLE_GAP * 2);
  for (let k = 5; k <= pts.length; k++) if (lastSegmentCrosses(pts.slice(0, k))) return true;
  return false;
}

/** 차단 점을 경계로 점열을 여러 조각으로. 빈 조각은 버린다. */
export function splitBlocked(pts: Pt[], blockers: Blocker[], margin: number): Pt[][] {
  const out: Pt[][] = [];
  let cur: Pt[] = [];
  for (const p of pts) {
    if (isBlocked(p, blockers, margin)) {
      if (cur.length) out.push(cur);
      cur = [];
    } else cur.push(p);
  }
  if (cur.length) out.push(cur);
  return out;
}

function pathLength(pts: Pt[]): number {
  let l = 0;
  for (let i = 1; i < pts.length; i++) l += Math.sqrt(distSq(pts[i]!, pts[i - 1]!));
  return l;
}

/** 원시 점열(월드 좌표) → 강체로 만들 조각들. 순서: 간격 필터 → 차단 분할 → 단순화 → 점/선 분류. */
/** 샘플이 성길 때(빠른 손, 테스트) 사이를 채워 차단 판정이 선분 전체를 보게 한다. */
export function densify(pts: Pt[], maxGap: number): Pt[] {
  if (pts.length < 2) return pts.slice();
  const out: Pt[] = [pts[0]!];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!, b = pts[i]!;
    const d = Math.sqrt(distSq(a, b));
    const n = Math.ceil(d / maxGap);
    for (let k = 1; k <= n; k++) out.push([a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n]);
  }
  return out;
}

export function buildSegments(raw: Pt[], blockers: Blocker[]): StrokeSegment[] {
  const pts = filterGap(densify(raw, MIN_SAMPLE_GAP * 2), MIN_SAMPLE_GAP);
  const pieces = splitBlocked(pts, blockers, STROKE_T / 2);
  const out: StrokeSegment[] = [];
  for (const piece of pieces) {
    const s = simplify(piece, DP_EPSILON);
    if (s.length < 2 || pathLength(s) < STROKE_T) {
      // 한 점 또는 두께보다 짧은 낙서 → 점
      const cx = s.reduce((a, p) => a + p[0], 0) / s.length;
      const cy = s.reduce((a, p) => a + p[1], 0) / s.length;
      out.push({ kind: 'dot', c: [cx, cy] });
    } else out.push({ kind: 'line', pts: s });
  }
  return out;
}
